import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from 'react-admin';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import { getCurrentCustomer } from '../../../lib/currentCustomer';
import { ClientConfigGroupAutocompleteOption, ClientConfigGroupChip } from '../../../components/clientConfig/ClientConfigGroupChip';
import authProvider from '../../../utils/authProvider';
import { typedHttpClient } from '../../../utils/httpClient';
import {
  AppDefinition,
  AppKeyDefinition,
  ChangedPreviewNode,
  areKeyDefinitionsEqual,
  buildChangedPreviewTree,
  buildConfigFromDefinitionLayersWithSources,
  buildGroupAttachmentLevelById,
  ClientConfigOsFilter,
  findKeyDefinitionByPath,
  flattenChangedLeafPaths,
  getChangedObject,
  getFirstAppConfigEntry,
  hasActiveGroupSourcesAtOrUnderPath,
  isClientConfigGroupVisibleForCustomer,
  removeKeyDefinitionByPath,
  splitDefinitions,
} from '../../../lib/clientConfigHelpers';
import {
  useAllClientConfigGroupsQuery,
} from '../../../lib/clientConfigReactQuery';
import { ClientConfigChangedPreviewTree } from '../../../components/clientConfig/ClientConfigChangedPreviewTree';
import { ClientConfigOsFilterControl } from '../../../components/clientConfig/ClientConfigOsFilterControl';

type TargetType = 'customer' | 'group' | 'device';

type Option = {
  id: string;
  name: string;
  label: string;
};

type BaselineMode = 'allCustomers' | 'thisCustomer' | 'thisGroup' | 'thisDevice';
type SourcesByPath = Record<string, string[]>;

type PendingTargetAction =
  | { type: 'customer'; value: Option | null }
  | { type: 'group'; value: Option | null }
  | { type: 'device'; value: Option | null };

const PENDING_BROWSER_BACK = '__back__';

const isModifiedNavigationClick = (event: MouseEvent) => (
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
);

const getInternalNavigationPath = (anchor: HTMLAnchorElement): string | null => {

  if (anchor.target && anchor.target !== '_self') {

    return null;

  }
  if (anchor.hasAttribute('download')) {

    return null;

  }

  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {

    return null;

  }

  let url: URL;
  try {

    url = new URL(anchor.href, window.location.href);

  } catch {

    return null;

  }

  if (url.origin !== window.location.origin) {

    return null;

  }

  const nextPath = `${url.pathname}${url.search}${url.hash}`;
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextPath === currentPath) {

    return null;

  }

  return nextPath;

};

const toOption = (record: any, fallbackName: string): Option => {

  const id = record?._id || record?.id;
  const name = record?.name || record?.deviceId || fallbackName;
  return {
    id,
    name,
    label: name,
  };

};

const getDeviceOptionLabel = (record: any, assetNameById: Record<string, string>): string => {

  const assetId = record?.asset?._id || record?.asset?.id;
  if (assetId) {

    const fromRecord = typeof record?.asset?.name === 'string' ? record.asset.name.trim() : '';
    if (fromRecord) {

      return fromRecord;

    }
    const fromMap = assetNameById[assetId];
    if (fromMap) {

      return fromMap;

    }

  }
  return record?.name || record?.deviceId || 'Device';

};

const deviceRecordToOption = (record: any, assetNameById: Record<string, string>): Option => {

  const id = record?._id || record?.id;
  const label = getDeviceOptionLabel(record, assetNameById);
  return {
    id,
    name: label,
    label,
  };

};

const resourceForType = (targetType: TargetType) => {

  if (targetType === 'customer') return 'customers';
  if (targetType === 'group') return 'asset-groups';
  return 'devices';

};

const buildGroupDefinitions = (groupIds: string[]): AppDefinition[] => groupIds.map((groupId) => ({
  type: 'group',
  group: { _id: groupId, _ref: 'ClientConfigGroup' },
}));

const getFirstStringValue = (values: unknown[]): string | null => {

  for (const value of values) {

    if (typeof value === 'string' && value.trim()) {

      return value;

    }

  }
  return null;

};

const getRecordCustomerId = (record: any): string | null => {

  if (!record) {

    return null;

  }
  return getFirstStringValue([
    record?.customer?._id,
    record?.customer?.id,
    typeof record?.customer === 'string' ? record.customer : null,
    record?.customerId,
    record?.xgacCustomerId,
    record?.xgacCustomer?._id,
    record?.xgacCustomer?.id,
    record?.parentCustomer?._id,
  ]);

};

const getAppDefinitionsFromRecord = (record: any): AppDefinition[] => (
  getFirstAppConfigEntry(record)?.definitions || []
);

const getAssetGroupIdsFromAsset = (asset: any): string[] => {

  const rawGroups = asset?.assetGroups;
  if (!Array.isArray(rawGroups)) {

    return [];

  }
  return rawGroups
    .map((entry: any) => {

      if (typeof entry === 'string') {

        return entry;

      }
      return entry?._id || entry?.id || null;

    })
    .filter((id: string | null): id is string => Boolean(id));

};

export const ClientConfigManagerPage = () => {

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const translate = useTranslate();
  const identity = useGetIdentity();
  const [searchParams] = useSearchParams();
  const isDevAdmin = authProvider.isDeveloperAdmin();
  const currentCustomer = getCurrentCustomer();
  const [lastSavedCustomer, setLastSavedCustomer] = useState<string | null>(null);

  const [customerOptions, setCustomerOptions] = useState<Option[]>([]);
  const [groupOptions, setGroupOptions] = useState<Option[]>([]);
  const [deviceOptions, setDeviceOptions] = useState<Option[]>([]);

  const {
    data: configGroupOptions = [],
    isError: configGroupsQueryFailed,
    error: configGroupsQueryError,
  } = useAllClientConfigGroupsQuery();

  useEffect(() => {

    if (!configGroupsQueryFailed || !configGroupsQueryError) {

      return;

    }
    console.error(configGroupsQueryError);
    notify('resources.config.text.defaults_update_error', { type: 'error' });

  }, [configGroupsQueryFailed, configGroupsQueryError, notify]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [selectedTargetType, setSelectedTargetType] = useState<TargetType | null>(null);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [showBaselineSettings, setShowBaselineSettings] = useState(false);
  const [baselineMode, setBaselineMode] = useState<BaselineMode>('allCustomers');

  const [baselineConfig, setBaselineConfig] = useState<Record<string, any> | null>(null);
  const [targetRecord, setTargetRecord] = useState<any>(null);
  const [customerInheritanceDefinitions, setCustomerInheritanceDefinitions] = useState<AppDefinition[]>([]);
  const [assetGroupInheritanceDefinitionLayers, setAssetGroupInheritanceDefinitionLayers] = useState<AppDefinition[][]>([]);
  const [initialKeyDefinitions, setInitialKeyDefinitions] = useState<AppKeyDefinition[]>([]);
  const [pendingKeyDefinitions, setPendingKeyDefinitions] = useState<AppKeyDefinition[]>([]);
  const [targetFilter, setTargetFilter] = useState<ClientConfigOsFilter | undefined>(undefined);
  const [initialTargetFilter, setInitialTargetFilter] = useState<ClientConfigOsFilter | undefined>(undefined);
  const [initialGroupIds, setInitialGroupIds] = useState<string[]>([]);
  const [selectedConfigGroupIds, setSelectedConfigGroupIds] = useState<string[]>([]);

  const [loadingLists, setLoadingLists] = useState(false);
  const [loadingTarget, setLoadingTarget] = useState(false);
  const [loadingBaseline, setLoadingBaseline] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const [pendingTargetAction, setPendingTargetAction] = useState<PendingTargetAction | null>(null);
  const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);

  const selectedCustomer = useMemo(
    () => customerOptions.find((option) => option.id === selectedCustomerId) || null,
    [customerOptions, selectedCustomerId],
  );
  const selectedGroup = useMemo(
    () => groupOptions.find((option) => option.id === selectedGroupId) || null,
    [groupOptions, selectedGroupId],
  );
  const selectedDevice = useMemo(
    () => deviceOptions.find((option) => option.id === selectedDeviceId) || null,
    [deviceOptions, selectedDeviceId],
  );

  const configGroupById = useMemo(() => _.keyBy(configGroupOptions, '_id'), [configGroupOptions]);

  const selectedConfigGroups = useMemo(() => configGroupOptions.filter((group) => selectedConfigGroupIds.includes(group._id)), [
    configGroupOptions,
    selectedConfigGroupIds,
  ]);
  const derivedCustomerId = useMemo(() => {

    if (selectedTargetType === 'customer') {

      return selectedTargetId;

    }
    return getRecordCustomerId(targetRecord);

  }, [selectedTargetType, selectedTargetId, targetRecord]);
  const scopedCustomerId = derivedCustomerId || getCurrentCustomer()?.value || null;
  const selectableConfigGroupOptions = useMemo(
    () => configGroupOptions.filter((group) => isClientConfigGroupVisibleForCustomer(group, scopedCustomerId)),
    [configGroupOptions, scopedCustomerId],
  );
  const canUseThisCustomer = Boolean(derivedCustomerId);
  const canUseThisGroup = Boolean(selectedTargetType === 'group' && selectedTargetId);
  const canUseThisDevice = Boolean(selectedTargetType === 'device' && selectedTargetId);
  const showEntityLocalOnly = (
    (baselineMode === 'thisCustomer' && selectedTargetType === 'customer')
    || (baselineMode === 'thisGroup' && selectedTargetType === 'group')
    || (baselineMode === 'thisDevice' && selectedTargetType === 'device')
  );
  // Layers between the compare baseline and the selected target (customer → each asset group → target).
  const inheritanceDefinitionLayers = useMemo(() => {

    if (showEntityLocalOnly) {

      return [] as AppDefinition[][];

    }
    if (baselineMode === 'allCustomers') {

      return [customerInheritanceDefinitions, ...assetGroupInheritanceDefinitionLayers];

    }
    if (baselineMode === 'thisCustomer') {

      // Baseline already includes customer config; only apply asset-group layers above a device.
      return [...assetGroupInheritanceDefinitionLayers];

    }
    return [] as AppDefinition[][];

  }, [
    assetGroupInheritanceDefinitionLayers,
    baselineMode,
    customerInheritanceDefinitions,
    showEntityLocalOnly,
  ]);
  // Apply inheritance + local defs onto the selected compare baseline (or empty for own-level view).
  const applyBaseConfig = useMemo(
    () => (showEntityLocalOnly ? {} : baselineConfig),
    [showEntityLocalOnly, baselineConfig],
  );
  const diffBaseConfig = useMemo(
    () => (showEntityLocalOnly ? {} : baselineConfig),
    [showEntityLocalOnly, baselineConfig],
  );
  const groupAttachmentLevelById = useMemo(() => {

    if (showEntityLocalOnly || !selectedTargetType) {

      return {};

    }
    // Same layer selection as inheritanceDefinitionLayers (customer only when comparing to global).
    let attachmentLayers: Array<{
      level: 'customer' | 'group';
      definitions: AppDefinition[];
    }>;
    if (baselineMode === 'allCustomers') {

      attachmentLayers = [
        { level: 'customer', definitions: customerInheritanceDefinitions },
        ...assetGroupInheritanceDefinitionLayers.map((definitions) => ({
          level: 'group' as const,
          definitions,
        })),
      ];

    } else if (baselineMode === 'thisCustomer') {

      attachmentLayers = assetGroupInheritanceDefinitionLayers.map((definitions) => ({
        level: 'group' as const,
        definitions,
      }));

    } else {

      attachmentLayers = [];

    }
    return buildGroupAttachmentLevelById([
      ...attachmentLayers,
      { level: selectedTargetType, definitions: buildGroupDefinitions(selectedConfigGroupIds) },
    ], configGroupById);

  }, [
    assetGroupInheritanceDefinitionLayers,
    baselineMode,
    configGroupById,
    customerInheritanceDefinitions,
    selectedConfigGroupIds,
    selectedTargetType,
    showEntityLocalOnly,
  ]);

  const hasGroupChanges = !_.isEqual([...initialGroupIds].sort(), [...selectedConfigGroupIds].sort());
  const hasKeyChanges = !areKeyDefinitionsEqual(initialKeyDefinitions, pendingKeyDefinitions);
  const hasFilterChanges = !_.isEqual(initialTargetFilter, targetFilter);
  const hasPendingChanges = hasGroupChanges || hasKeyChanges || hasFilterChanges;
  const addedGroupIds = useMemo(
    () => new Set(selectedConfigGroupIds.filter((groupId) => !initialGroupIds.includes(groupId))),
    [initialGroupIds, selectedConfigGroupIds],
  );
  const removedGroupIds = useMemo(
    () => new Set(initialGroupIds.filter((groupId) => !selectedConfigGroupIds.includes(groupId))),
    [initialGroupIds, selectedConfigGroupIds],
  );

  useEffect(() => {

    if (!hasPendingChanges) {

      return () => {};

    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {

      event.preventDefault();
      event.returnValue = '';

    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {

      window.removeEventListener('beforeunload', handleBeforeUnload);

    };

  }, [hasPendingChanges]);

  useEffect(() => {

    if (!hasPendingChanges) {

      return () => {};

    }

    const handleClick = (event: MouseEvent) => {

      if (unsavedDialogOpen) {

        return;

      }
      if (event.defaultPrevented || event.button !== 0 || isModifiedNavigationClick(event)) {

        return;

      }

      const anchor = (event.target as Element | null)?.closest('a');
      if (!anchor) {

        return;

      }

      const nextPath = getInternalNavigationPath(anchor);
      if (!nextPath) {

        return;

      }

      event.preventDefault();
      event.stopPropagation();
      setPendingTargetAction(null);
      setPendingNavigationPath(nextPath);
      setUnsavedDialogOpen(true);

    };

    document.addEventListener('click', handleClick, true);
    return () => {

      document.removeEventListener('click', handleClick, true);

    };

  }, [hasPendingChanges, unsavedDialogOpen]);

  useEffect(() => {

    if (!hasPendingChanges) {

      return () => {};

    }

    const handlePopState = () => {

      window.history.pushState({ clientConfigUnsavedGuard: true }, '', window.location.href);
      setPendingTargetAction(null);
      setPendingNavigationPath(PENDING_BROWSER_BACK);
      setUnsavedDialogOpen(true);

    };

    window.history.pushState({ clientConfigUnsavedGuard: true }, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => {

      window.removeEventListener('popstate', handlePopState);

    };

  }, [hasPendingChanges]);

  useEffect(() => {

    if (!isDevAdmin) {

      notify('resources.config.text.no_access');
      navigate(-1);

    }

  }, [isDevAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGroupsAndDevices = useCallback(async () => {

    setLoadingLists(true);
    try {

      const [assetGroups, devices] = await Promise.all([
        dataProvider.getList('asset-groups', {
          pagination: { page: 1, perPage: -1 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        }),
        dataProvider.getList('devices', {
          pagination: { page: 1, perPage: -1 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        }),
      ]);

      setGroupOptions((assetGroups.data || []).map((record: any) => toOption(record, 'Group')));
      const deviceRecords = devices.data || [];
      const assetIdsToResolve = _.uniq(
        deviceRecords
          .filter((record: any) => {

            const assetId = record?.asset?._id || record?.asset?.id;
            if (!assetId) {

              return false;

            }
            return !(typeof record?.asset?.name === 'string' && record.asset.name.trim());

          })
          .map((record: any) => record.asset._id || record.asset.id),
      );
      const assetNameById: Record<string, string> = {};
      if (assetIdsToResolve.length > 0) {

        const assetsResponse = await dataProvider.getMany('assets', { ids: assetIdsToResolve });
        (assetsResponse.data || []).forEach((asset: any) => {

          const assetId = asset?._id || asset?.id;
          if (assetId && typeof asset?.name === 'string') {

            assetNameById[assetId] = asset.name;

          }

        });

      }
      setDeviceOptions(deviceRecords.map((record: any) => deviceRecordToOption(record, assetNameById)));

    } catch (error) {

      console.error(error);
      notify('resources.config.text.defaults_update_error', { type: 'error' });

    } finally {

      setLoadingLists(false);

    }

  }, [dataProvider, notify]);

  useEffect(() => {

    const fetchCustomers = async () => {

      try {

        const customers = await dataProvider.getList('customers', {
          pagination: { page: 1, perPage: -1 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        });
        setCustomerOptions((customers.data || []).map((record: any) => toOption(record, 'Customer')));

      } catch (error) {

        console.error(error);
        notify('resources.config.text.defaults_update_error', { type: 'error' });

      }

    };

    fetchCustomers();

  }, [dataProvider, notify]);

  useEffect(() => {

    const customerId = currentCustomer?.value ?? null;
    if (!customerId) {

      return;

    }
    if (lastSavedCustomer === customerId) {

      return;

    }

    const isCustomerChange = lastSavedCustomer !== null;
    if (isCustomerChange) {

      setSelectedGroupId(null);
      setSelectedDeviceId(null);
      setGroupOptions([]);
      setDeviceOptions([]);
      if (selectedTargetType === 'group' || selectedTargetType === 'device') {

        setSelectedTargetType(null);
        setSelectedTargetId(null);

      }

    }
    setLastSavedCustomer(customerId);
    fetchGroupsAndDevices();

  }, [currentCustomer?.value, identity, fetchGroupsAndDevices]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    const customerId = searchParams.get('customer');
    const groupId = searchParams.get('group');
    const deviceId = searchParams.get('device');

    if (deviceId) {

      setSelectedDeviceId(deviceId);
      setSelectedCustomerId(null);
      setSelectedGroupId(null);
      setSelectedTargetType('device');
      setSelectedTargetId(deviceId);
      return;

    }
    if (groupId) {

      setSelectedGroupId(groupId);
      setSelectedCustomerId(null);
      setSelectedDeviceId(null);
      setSelectedTargetType('group');
      setSelectedTargetId(groupId);
      return;

    }
    if (customerId) {

      setSelectedCustomerId(customerId);
      setSelectedGroupId(null);
      setSelectedDeviceId(null);
      setSelectedTargetType('customer');
      setSelectedTargetId(customerId);

    }

  }, [searchParams]);

  // Always start comparison at the highest baseline; users can still pick a lower one in settings.
  useEffect(() => {

    setBaselineMode('allCustomers');

  }, [selectedTargetType]);

  useEffect(() => {

    let cancelled = false;

    const loadTarget = async () => {

      if (!selectedTargetType || !selectedTargetId) {

        setLoadingTarget(false);
        setTargetRecord(null);
        setCustomerInheritanceDefinitions([]);
        setAssetGroupInheritanceDefinitionLayers([]);
        setInitialKeyDefinitions([]);
        setPendingKeyDefinitions([]);
        setSelectedConfigGroupIds([]);
        setInitialGroupIds([]);
        setTargetFilter(undefined);
        setInitialTargetFilter(undefined);
        return;

      }

      setLoadingTarget(true);
      setCustomerInheritanceDefinitions([]);
      setAssetGroupInheritanceDefinitionLayers([]);
      setInitialKeyDefinitions([]);
      setPendingKeyDefinitions([]);
      setSelectedConfigGroupIds([]);
      setInitialGroupIds([]);
      try {

        const resource = resourceForType(selectedTargetType);
        const target = await dataProvider.getOne(resource, { id: selectedTargetId });
        if (cancelled) {

          return;

        }

        const appEntry = getFirstAppConfigEntry(target.data);
        const definitions = appEntry?.definitions || [];
        const { groupDefinitions, keyDefinitions } = splitDefinitions(definitions);
        const existingGroupIds = groupDefinitions.map((definition) => definition.group._id);

        let customerDefinitions: AppDefinition[] = [];
        let assetGroupDefinitionLayers: AppDefinition[][] = [];
        if (selectedTargetType !== 'customer') {

          const customerId = getRecordCustomerId(target.data);
          if (customerId) {

            const customer = await dataProvider.getOne('customers', { id: customerId });
            if (cancelled) {

              return;

            }
            customerDefinitions = getAppDefinitionsFromRecord(customer.data);

          }

        }
        if (selectedTargetType === 'device') {

          const assetId = target.data?.asset?._id || target.data?.asset?.id;
          if (assetId) {

            const asset = await dataProvider.getOne('assets', { id: assetId });
            if (cancelled) {

              return;

            }
            const assetGroupIds = getAssetGroupIdsFromAsset(asset.data);
            if (assetGroupIds.length > 0) {

              const assetGroups = await dataProvider.getMany('asset-groups', { ids: assetGroupIds });
              if (cancelled) {

                return;

              }
              const assetGroupById = _.keyBy(assetGroups.data || [], (record: any) => record?._id || record?.id);
              // One layer per asset group (matches main-api sequential sources).
              assetGroupDefinitionLayers = assetGroupIds.map((assetGroupId) => (
                getAppDefinitionsFromRecord(assetGroupById[assetGroupId])
              ));

            }

          }

        }

        if (cancelled) {

          return;

        }
        setTargetRecord(target.data);
        setCustomerInheritanceDefinitions(customerDefinitions);
        setAssetGroupInheritanceDefinitionLayers(assetGroupDefinitionLayers);
        setInitialKeyDefinitions(keyDefinitions);
        setPendingKeyDefinitions(keyDefinitions);
        setTargetFilter(appEntry?.filter);
        setInitialTargetFilter(appEntry?.filter);
        setSelectedConfigGroupIds(existingGroupIds);
        setInitialGroupIds(existingGroupIds);

      } catch (error) {

        if (cancelled) {

          return;

        }
        console.error(error);
        setTargetRecord(null);
        setCustomerInheritanceDefinitions([]);
        setAssetGroupInheritanceDefinitionLayers([]);
        setInitialKeyDefinitions([]);
        setPendingKeyDefinitions([]);
        setSelectedConfigGroupIds([]);
        setInitialGroupIds([]);
        notify('resources.config.text.defaults_update_error', { type: 'error' });

      } finally {

        if (!cancelled) {

          setLoadingTarget(false);

        }

      }

    };

    loadTarget();
    return () => {

      cancelled = true;

    };

  }, [selectedTargetId, selectedTargetType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    const loadBaseline = async () => {

      if (!selectedTargetType || !selectedTargetId) {

        setBaselineConfig(null);
        return;

      }
      setLoadingBaseline(true);
      try {

        let response;
        if (baselineMode === 'allCustomers') {

          response = await typedHttpClient().clientConfig.clientConfigControllerResolveAppDefaultConfigV0();

        } else if (baselineMode === 'thisCustomer') {

          if (!derivedCustomerId) {

            notify('clientConfig.manager.baseline.customerUnavailable', { type: 'warning' });
            setBaselineConfig(null);
            return;

          }
          response = await typedHttpClient().clientConfig.clientConfigControllerResolveAppCustomerConfigV0(derivedCustomerId);

        } else if (baselineMode === 'thisGroup') {

          if (!canUseThisGroup) {

            notify('clientConfig.manager.baseline.groupUnavailable', { type: 'warning' });
            setBaselineConfig(null);
            return;

          }
          response = await typedHttpClient().clientConfig.clientConfigControllerResolveAppAssetGroupConfigV0(selectedTargetId);

        } else {

          if (!canUseThisDevice) {

            notify('clientConfig.manager.baseline.deviceUnavailable', { type: 'warning' });
            setBaselineConfig(null);
            return;

          }
          response = await typedHttpClient().clientConfig.clientConfigControllerResolveAppDeviceConfigV0(selectedTargetId);

        }
        setBaselineConfig(response.data?.config || {});

      } catch (error) {

        console.error(error);
        notify('resources.config.text.defaults_update_error', { type: 'error' });

      } finally {

        setLoadingBaseline(false);

      }

    };

    loadBaseline();

  }, [
    baselineMode,
    canUseThisDevice,
    canUseThisGroup,
    derivedCustomerId,
    notify,
    selectedTargetId,
    selectedTargetType,
  ]);

  const previewData = useMemo(() => {

    if (applyBaseConfig == null || (!showEntityLocalOnly && diffBaseConfig == null)) {

      return {
        previewConfig: {},
        sourcesByPath: {} as SourcesByPath,
        overriddenSourcesByPath: {} as SourcesByPath,
      };

    }
    if (!selectedTargetType || !selectedTargetId) {

      return {
        previewConfig: {},
        sourcesByPath: {} as SourcesByPath,
        overriddenSourcesByPath: {} as SourcesByPath,
      };

    }

    const localDefinitions: AppDefinition[] = [
      ...pendingKeyDefinitions,
      ...buildGroupDefinitions(selectedConfigGroupIds),
    ];
    const layers = showEntityLocalOnly
      ? [localDefinitions]
      : [...inheritanceDefinitionLayers, localDefinitions];
    const result = buildConfigFromDefinitionLayersWithSources(applyBaseConfig, layers, configGroupById);
    return {
      previewConfig: result.config,
      sourcesByPath: result.sourcesByPath,
      overriddenSourcesByPath: result.overriddenSourcesByPath,
    };

  }, [
    inheritanceDefinitionLayers,
    applyBaseConfig,
    configGroupById,
    diffBaseConfig,
    pendingKeyDefinitions,
    selectedConfigGroupIds,
    selectedTargetId,
    selectedTargetType,
    showEntityLocalOnly,
  ]);

  const changedKeysObject = useMemo(() => {

    if (diffBaseConfig == null) {

      return {};

    }
    return getChangedObject(diffBaseConfig, previewData.previewConfig) || {};

  }, [diffBaseConfig, previewData.previewConfig]);

  const previousPreviewData = useMemo<{
    previewConfig: Record<string, any>;
    sourcesByPath: SourcesByPath;
    overriddenSourcesByPath: SourcesByPath;
  }>(() => {

    if (applyBaseConfig == null || (!showEntityLocalOnly && diffBaseConfig == null)
      || !selectedTargetType || !selectedTargetId) {

      return {
        previewConfig: {},
        sourcesByPath: {},
        overriddenSourcesByPath: {},
      };

    }
    const localDefinitions: AppDefinition[] = [
      ...initialKeyDefinitions,
      ...buildGroupDefinitions(initialGroupIds),
    ];
    const layers = showEntityLocalOnly
      ? [localDefinitions]
      : [...inheritanceDefinitionLayers, localDefinitions];
    const result = buildConfigFromDefinitionLayersWithSources(applyBaseConfig, layers, configGroupById);
    return {
      previewConfig: result.config,
      sourcesByPath: result.sourcesByPath,
      overriddenSourcesByPath: result.overriddenSourcesByPath,
    };

  }, [
    inheritanceDefinitionLayers,
    applyBaseConfig,
    configGroupById,
    diffBaseConfig,
    initialGroupIds,
    initialKeyDefinitions,
    selectedTargetId,
    selectedTargetType,
    showEntityLocalOnly,
  ]);
  const changedLeafPaths = useMemo(
    () => new Set(flattenChangedLeafPaths(changedKeysObject).map((entry) => entry.path).filter(Boolean)),
    [changedKeysObject],
  );
  const removedOnlyPaths = useMemo(
    () => Object.keys(previousPreviewData.sourcesByPath)
      .filter((path) => {

        const previousPathSources = previousPreviewData.sourcesByPath[path] || [];
        const currentPathSources = previewData.sourcesByPath[path] || [];
        const hadRemovedGroupBefore = previousPathSources.some((groupId) => removedGroupIds.has(groupId));
        return hadRemovedGroupBefore && currentPathSources.length === 0 && !changedLeafPaths.has(path);

      }),
    [changedLeafPaths, previewData.sourcesByPath, previousPreviewData.sourcesByPath, removedGroupIds],
  );
  const removedInlineKeyPaths = useMemo(() => {

    if (selectedTargetType !== 'device') {

      return [];

    }
    const pendingKeyPaths = new Set(pendingKeyDefinitions.map((definition) => definition.key));
    const removedOnlyPathSet = new Set(removedOnlyPaths);
    return initialKeyDefinitions
      .filter((definition) => !pendingKeyPaths.has(definition.key))
      .map((definition) => definition.key)
      .filter((path) => !removedOnlyPathSet.has(path));

  }, [
    initialKeyDefinitions,
    pendingKeyDefinitions,
    removedOnlyPaths,
    selectedTargetType,
  ]);
  const removedInlineKeyPathSet = useMemo(
    () => new Set(removedInlineKeyPaths),
    [removedInlineKeyPaths],
  );
  const savedChangedObject = useMemo(() => {

    if (diffBaseConfig == null) {

      return {};

    }
    return getChangedObject(diffBaseConfig, previousPreviewData.previewConfig) || {};

  }, [diffBaseConfig, previousPreviewData.previewConfig]);
  const combinedChangedObject = useMemo(() => {

    const merged = _.cloneDeep(savedChangedObject);
    const excludedPathSet = new Set([
      ...removedOnlyPaths,
      ...removedInlineKeyPaths,
    ]);
    flattenChangedLeafPaths(changedKeysObject).forEach(({ path, value }) => {

      if (!excludedPathSet.has(path)) {

        _.set(merged, path, value);

      }

    });
    excludedPathSet.forEach((path) => {

      const savedValue = _.get(savedChangedObject, path);
      const currentPreviewValue = _.get(previewData.previewConfig, path);
      const isRemovedInline = removedInlineKeyPaths.includes(path);
      const groupStillSourcesPath = hasActiveGroupSourcesAtOrUnderPath(previewData.sourcesByPath, path);

      if (isRemovedInline && groupStillSourcesPath) {

        _.set(merged, path, currentPreviewValue);
        return;

      }

      if (savedValue !== undefined) {

        _.set(merged, path, savedValue);
        return;

      }
      _.set(merged, path, _.get(previousPreviewData.previewConfig, path));

    });
    return merged;

  }, [
    changedKeysObject,
    previewData.previewConfig,
    previewData.sourcesByPath,
    previousPreviewData.previewConfig,
    removedInlineKeyPaths,
    removedOnlyPaths,
    savedChangedObject,
  ]);
  const changedTree = useMemo<ChangedPreviewNode[]>(
    () => buildChangedPreviewTree(combinedChangedObject, previewData.sourcesByPath),
    [combinedChangedObject, previewData.sourcesByPath],
  );

  const setTarget = (targetType: TargetType | null, id: string | null) => {

    setSelectedTargetType(targetType);
    setSelectedTargetId(id);

  };

  const applyCustomerChange = (value: Option | null) => {

    setSelectedCustomerId(value?.id || null);
    setSelectedGroupId(null);
    setSelectedDeviceId(null);
    setTarget(value ? 'customer' : null, value?.id || null);

  };

  const applyGroupChange = (value: Option | null) => {

    setSelectedGroupId(value?.id || null);
    setSelectedCustomerId(null);
    setSelectedDeviceId(null);
    setTarget(value ? 'group' : null, value?.id || null);

  };

  const applyDeviceChange = (value: Option | null) => {

    setSelectedDeviceId(value?.id || null);
    setSelectedCustomerId(null);
    setSelectedGroupId(null);
    setTarget(value ? 'device' : null, value?.id || null);

  };

  const applyTargetAction = (action: PendingTargetAction) => {

    if (action.type === 'customer') {

      applyCustomerChange(action.value);

    } else if (action.type === 'group') {

      applyGroupChange(action.value);

    } else {

      applyDeviceChange(action.value);

    }

  };

  const requestTargetChange = (action: PendingTargetAction) => {

    if (hasPendingChanges) {

      setPendingTargetAction(action);
      setPendingNavigationPath(null);
      setUnsavedDialogOpen(true);
      return;

    }
    applyTargetAction(action);

  };

  const handleCustomerChange = (value: Option | null) => {

    requestTargetChange({ type: 'customer', value });

  };

  const handleGroupChange = (value: Option | null) => {

    requestTargetChange({ type: 'group', value });

  };

  const handleDeviceChange = (value: Option | null) => {

    requestTargetChange({ type: 'device', value });

  };

  const handleStayOnPage = () => {

    setUnsavedDialogOpen(false);
    setPendingTargetAction(null);
    setPendingNavigationPath(null);

  };

  const handleStageInlineKeyRemoval = useCallback((path: string) => {

    setPendingKeyDefinitions((previous) => removeKeyDefinitionByPath(previous, path));

  }, []);

  const handleRestoreInlineKey = useCallback((path: string) => {

    const definition = findKeyDefinitionByPath(initialKeyDefinitions, path);
    if (!definition) {

      return;

    }
    setPendingKeyDefinitions((previous) => {

      if (previous.some((entry) => entry.key === path)) {

        return previous;

      }
      return [...previous, definition];

    });

  }, [initialKeyDefinitions]);

  const handleSave = async (): Promise<boolean> => {

    if (!selectedTargetType || !selectedTargetId || !targetRecord) {

      return false;

    }

    setSaving(true);
    try {

      const resource = resourceForType(selectedTargetType);
      const existingAppConfig = Array.isArray(targetRecord?.clientConfig?.app) ? targetRecord.clientConfig.app : [];
      const firstEntry = existingAppConfig[0] || { definitions: [] };

      const payloadFirstEntry = {
        ...firstEntry,
        definitions: [
          ...pendingKeyDefinitions,
          ...buildGroupDefinitions(selectedConfigGroupIds),
        ],
      };
      if (targetFilter) {

        payloadFirstEntry.filter = targetFilter;

      } else {

        delete payloadFirstEntry.filter;

      }

      const data = {
        clientConfig: {
          ...(targetRecord?.clientConfig || {}),
          app: [payloadFirstEntry, ...existingAppConfig.slice(1)],
        },
      };

      await dataProvider.update(resource, {
        id: selectedTargetId,
        data,
        previousData: targetRecord,
      });

      setInitialGroupIds(selectedConfigGroupIds);
      setInitialKeyDefinitions(pendingKeyDefinitions);
      setInitialTargetFilter(targetFilter);
      setTargetRecord({
        ...targetRecord,
        clientConfig: data.clientConfig,
      });
      notify('resources.app-users.text.saving_group_successful', { type: 'success' });
      return true;

    } catch (error) {

      console.error(error);
      notify('resources.app-users.text.saving_group_failed', { type: 'error' });
      return false;

    } finally {

      setSaving(false);

    }

  };

  const handleSaveAndContinue = async () => {

    const saved = await handleSave();
    if (!saved) {

      return;

    }

    setUnsavedDialogOpen(false);

    if (pendingTargetAction) {

      const action = pendingTargetAction;
      setPendingTargetAction(null);
      applyTargetAction(action);

    } else if (pendingNavigationPath) {

      const nextPath = pendingNavigationPath;
      setPendingNavigationPath(null);
      if (nextPath === PENDING_BROWSER_BACK) {

        navigate(-1);

      } else {

        navigate(nextPath);

      }

    }

  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {translate('clientConfig.manager.title')}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {loadingLists ? (
            <CircularProgress size={24} />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={customerOptions}
                  value={selectedCustomer}
                  onChange={(_event, value) => handleCustomerChange(value)}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('clientConfig.manager.customer')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={groupOptions}
                  value={selectedGroup}
                  onChange={(_event, value) => handleGroupChange(value)}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('clientConfig.manager.group')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={deviceOptions}
                  value={selectedDevice}
                  onChange={(_event, value) => handleDeviceChange(value)}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('clientConfig.manager.device')}
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {selectedTargetType && selectedTargetId && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1,
                }}>
                  <Typography variant="h6">
                    {translate('clientConfig.manager.assignedGroups')}
                  </Typography>
                  {!loadingTarget && (
                    <ClientConfigOsFilterControl
                      value={targetFilter}
                      onChange={setTargetFilter}
                      helpTextKey="clientConfig.osFilter.targetFilterHelp"
                    />
                  )}
                </Box>
                {loadingTarget ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <Autocomplete
                      multiple
                      options={selectableConfigGroupOptions}
                      value={selectedConfigGroups}
                      onChange={(_event, values) => setSelectedConfigGroupIds(values.map((value) => value._id))}
                      getOptionLabel={(option) => option.name}
                      renderTags={(value, getTagProps) => value.map((option, index) => (
                        <ClientConfigGroupChip
                          key={option._id}
                          group={option}
                          getTagProps={getTagProps}
                          index={index}
                        />
                      ))}
                      renderOption={(optionProps, option) => (
                        <ClientConfigGroupAutocompleteOption
                          optionProps={optionProps}
                          group={option}
                        />
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={translate('clientConfig.manager.selectGroups')}
                        />
                      )}
                    />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'end' }}>
                      <Button variant="contained" onClick={handleSave} disabled={!hasPendingChanges || saving}>
                        {translate('ra.action.save')}
                      </Button>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2,
                }}>
                  <Typography variant="h6">
                    {translate('clientConfig.manager.changedKeys')}
                  </Typography>
                  <Tooltip title={translate('clientConfig.manager.baseline.settings')}>
                    <IconButton size="small" onClick={() => setShowBaselineSettings((previous) => !previous)}>
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Collapse in={showBaselineSettings}>
                  <Box sx={{ mb: 2 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="baseline-mode-label">{translate('clientConfig.manager.baseline.selectLabel')}</InputLabel>
                      <Select
                        labelId="baseline-mode-label"
                        value={baselineMode}
                        label={translate('clientConfig.manager.baseline.selectLabel')}
                        onChange={(event) => setBaselineMode(event.target.value as BaselineMode)}
                      >
                        <MenuItem value="allCustomers">{translate('clientConfig.manager.baseline.mode.allCustomers')}</MenuItem>
                        <MenuItem value="thisCustomer" disabled={!canUseThisCustomer}>
                          {translate('clientConfig.manager.baseline.mode.thisCustomer')}
                        </MenuItem>
                        <MenuItem value="thisGroup" disabled={!canUseThisGroup}>
                          {translate('clientConfig.manager.baseline.mode.thisGroup')}
                        </MenuItem>
                        <MenuItem value="thisDevice" disabled={!canUseThisDevice}>
                          {translate('clientConfig.manager.baseline.mode.thisDevice')}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Collapse>
                {loadingTarget || loadingBaseline ? (
                  <CircularProgress size={24} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {changedTree.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {translate('clientConfig.groups.preview.noChanges')}
                      </Typography>
                    )}
                    <ClientConfigChangedPreviewTree
                      roots={changedTree}
                      sourcesByPath={previewData.sourcesByPath}
                      overriddenSourcesByPath={previewData.overriddenSourcesByPath}
                      previousSourcesByPath={previousPreviewData.sourcesByPath}
                      removedGroupIds={removedGroupIds}
                      addedGroupIds={addedGroupIds}
                      removedInlineKeyPathSet={removedInlineKeyPathSet}
                      configGroupById={configGroupById}
                      groupAttachmentLevelById={groupAttachmentLevelById}
                      showGroupAttachmentLevel={!showEntityLocalOnly}
                      selectedTargetType={selectedTargetType}
                      initialKeyDefinitions={initialKeyDefinitions}
                      onRestoreInlineKey={handleRestoreInlineKey}
                      onStageInlineKeyRemoval={handleStageInlineKeyRemoval}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      <Dialog open={unsavedDialogOpen} onClose={handleStayOnPage}>
        <DialogTitle>{translate('clientConfig.manager.unsavedChanges.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translate('clientConfig.manager.unsavedChanges.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStayOnPage}>
            {translate('clientConfig.manager.unsavedChanges.stay')}
          </Button>
          <Button variant="contained" onClick={handleSaveAndContinue} disabled={saving}>
            {translate('clientConfig.manager.unsavedChanges.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

};
